import React, { forwardRef, useMemo } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import cx from 'classnames';

import EditableText from 'components/shared/EditableText';
import { useTitleChangeModal } from 'hooks/useTitleChangeModal';

import { EOrganizedBy } from '../../../structure';
import { useLandingV2Context } from '../../context';
import { getPrefix } from '../../utils';
import { usePageContext } from '../context';
import { EPageRole } from '../types';
import BlocksContent from './BlocksContent';
import { useRightSide } from './functions';
import s from './index.module.css';
import { NavigationArrow } from './NavigationArrow';

export const RightSide = forwardRef<HTMLDivElement>((props, ref) => {
  const { contentRef } = useLandingV2Context();
  const { handleIntersection, rightSideProps, className } = useRightSide();
  const { data, pageHeadlineRef, config, virtualized } = usePageContext();

  return (
    <Observer onChange={handleIntersection} rootMargin="-50% 0% -50% 0%" root={contentRef.current}>
      <div className={className} ref={ref} {...rightSideProps}>
        {!virtualized || virtualized.rendered ? (
          <>
            {config.hiddenStickyHeader !== true && <PageHeader ref={pageHeadlineRef} />}
            <div className={cx(s.inner, config.centeredContent && s.centered)}>
              <BlocksContent />
              {data.role === EPageRole.COVER_PAGE && <NavigationArrow />}
            </div>
          </>
        ) : null}
      </div>
    </Observer>
  );
});

const PageHeader = forwardRef<HTMLDivElement>((_props, ref) => {
  const { readonly: landingReadonly, data: proposal } = useLandingV2Context();
  const { data, onChange, readonly } = usePageContext();
  const showNavigationModal = useTitleChangeModal();
  const {
    settings: { textOverImages },
    scheduleType,
  } = proposal;
  const type = textOverImages ? 'title' : 'headline';
  const value = useMemo(() => {
    if (type === 'title') {
      return data.subTitle ? `${data.title}\n${data.subTitle}` : data.title;
    }

    if (scheduleType === EOrganizedBy.Date && data.role === EPageRole.DAY) {
      return `${data.headline}\n${data.subheadline}`;
    } else {
      return data.headline;
    }
  }, [data.headline, data.role, data.subTitle, data.subheadline, data.title, scheduleType, type]);

  const showTitle = !landingReadonly || (landingReadonly && value);

  const handleHeaderClick = () => {
    if (data.role !== EPageRole.COVER_PAGE) return;
    if (textOverImages) return;

    showNavigationModal(readonly);
  };

  return (
    <div className={cx(s.headerTitle, getPrefix('page-header'))} ref={ref} onClick={handleHeaderClick}>
      {showTitle && (
        <EditableText
          text={value}
          onChange={(text) => onChange?.({ [type]: text })}
          isReadonly={data.type === 'day' || readonly}
          textAlignment="center"
        />
      )}
    </div>
  );
});
